import { Text } from '@lmc/ui-kit/src/general'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'
import { WarningIcon } from 'screens/Rostering/RotaBuilder/common/components/icons'
import styled from 'styled-components'
import EventsPopover from './EventsPopover'
import TeamMembersPopover from './TeamMembersPopover'

export enum WarningType {
  DoubleBooked = 'doubleBooked',
  MissedShifts = 'missedShifts',
  NotYetClockedIn = 'notYetClockedInShifts',
  InvalidGeofencingAddresses = 'invalidGeofencingAddresses',
  SkillMismatch = 'skillMismatch',
  GenderMismatch = 'genderMismatch',
  LateClockIns = 'lateClockIns',
  EarlyClockOuts = 'earlyClockOuts',
  DurationMismatches = 'durationMismatches',
  ClockInLocationMismatch = 'clockInLocationMismatches',
  ClockOutLocationMismatch = 'clockOutLocationMismatches'
}

type WarningProps = {
  warningType: WarningType
  warning: any
}

const Warning = ({ warningType, warning }: WarningProps) => {
  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_parked_features',
      'rota_warnings_email_notifications'
    ]
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const flagsRotaWarningsEmailNotifications =
    flags?.rota_warnings_email_notifications?.enabled

  if (
    warningType === WarningType.NotYetClockedIn &&
    !flagsRotaWarningsEmailNotifications
  ) {
    return null
  }

  const warningTypeToTextMap: Partial<Record<WarningType, string>> = {
    [WarningType.DoubleBooked]: t('DOUBLE_BOOKED'),
    [WarningType.MissedShifts]: t('MISSED'),
    ...(flagsRotaWarningsEmailNotifications && {
      [WarningType.NotYetClockedIn]: t('NOT_YET_CLOCKED_IN')
    }),
    [WarningType.SkillMismatch]: t('SKILL_MISMATCH'),
    ...(!flagsRosteringParkedFeaturesEnabled && {
      [WarningType.GenderMismatch]: t('GENDER_MISMATCH')
    }),
    [WarningType.InvalidGeofencingAddresses]: t('INVALID_GEOFENCING_ADDRESSES'),
    [WarningType.LateClockIns]: t('CLOCKED_IN_LATE'),
    [WarningType.EarlyClockOuts]: t('CLOCKED_OUT_EARLY'),
    [WarningType.DurationMismatches]: t('DURATION_MISMATCH'),
    [WarningType.ClockInLocationMismatch]: t('CLOCKED_IN_LOCATION_MISMATCH'),
    [WarningType.ClockOutLocationMismatch]: t('CLOCKED_OUT_LOCATION_MISMATCH')
  }

  return warning.count === 0 ? (
    <Container noWarnings>
      <WarningContainer>
        <Text>{warningTypeToTextMap[warningType]}</Text>
      </WarningContainer>
      <Text weight="medium">{warning.count}</Text>
    </Container>
  ) : (
    <>
      {warning.events && (
        <EventsPopover events={warning.events}>
          <Container noWarnings={false}>
            <WarningContainer>
              <WarningIcon />
              <Text>{warningTypeToTextMap[warningType]}</Text>
            </WarningContainer>
            <Text weight="medium">{warning.count}</Text>
          </Container>
        </EventsPopover>
      )}{' '}
      {warning.users && (
        <TeamMembersPopover users={warning.users}>
          <Container noWarnings={false}>
            <WarningContainer>
              <WarningIcon />
              <Text>{warningTypeToTextMap[warningType]}</Text>
            </WarningContainer>
            <Text weight="medium">{warning.count}</Text>
          </Container>
        </TeamMembersPopover>
      )}
    </>
  )
}

const Container = styled.div<{ noWarnings: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${(props) =>
    props.noWarnings
      ? props.theme.color.neutral.color5
      : props.theme.color.neutral.color3};

  &:hover {
    cursor: default;
    color: ${(props) =>
      props.noWarnings
        ? props.theme.color.neutral.color5
        : props.theme.color.hover.primary};
  }
`
const WarningContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export default Warning
